import * as React from "react";
import {
  Grid,
  GridColumn as Column,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { filterBy } from "@progress/kendo-data-query";
import ErrorBanner from "../../components/ErrorBanner";
import DeleteDialog from "../../components/DeleteDialog";
import {
  useQueryGetPlanMappingPage,
  useQueryGetPlanListForMapping,
  useMutatePostPlanMapping,
  useMutatePutPlanMapping,
  useMutateDeletePlanMapping,
} from "../../data/services/benefitPlanService";
import { useQueryGetCarrierList } from "../../data/services/carrierService";
import { createPortal } from "react-dom";

const InputCell = (props) => {
  const { dataItem, field } = props;
  return (
    <td>
      {dataItem.inEdit ? (
        <input
          className="k-textbox k-input k-rounded-md"
          value={dataItem[field] || ""}
          onChange={(e) =>
            props.onChange({ dataItem, field, value: e.target.value })
          }
        />
      ) : (
        dataItem[field]
      )}
    </td>
  );
};

const CarrierCell = ({ dataItem, field, onChange, carrierList }) => (
  <td>
    {dataItem.inEdit ? (
      <DropDownList
        data={carrierList}
        textField="carrierName"
        value={carrierList.find((x) => x.carrierName === dataItem.carrierName)}
        onChange={(e) =>
          onChange({ dataItem, field, value: e.target.value.carrierName })
        }
      />
    ) : (
      dataItem.carrierName
    )}
  </td>
);

const PlanListCell = ({ dataItem, field, onChange, planList }) => {
  const [planIntData, setPlanIntData] = React.useState(planList);
  React.useEffect(() => {
    setPlanIntData(planList);
  }, [planList]);

  return (
    <td>
      {dataItem.inEdit ? (
        <DropDownList
          filterable={true}
          onFilterChange={(e) => setPlanIntData(filterBy(planList, e.filter))}
          data={planIntData}
          textField="PlanDisName"
          value={planList.find((x) => x.planId === dataItem.prismPlanId)}
          onChange={(e) =>
            onChange({ dataItem, field, value: e.target.value.planId })
          }
        />
      ) : (
        planList.find((x) => x.planId === dataItem.prismPlanId)?.PlanDisName ||
        dataItem.prismPlanId
      )}
    </td>
  );
};

const PlanMapping = () => {
  const editField = "inEdit";
  const [gridDataState, setGridDataState] = React.useState({
    take: 10,
    skip: 0,
    sort: [],
  });
  const page = Math.floor(gridDataState.skip / gridDataState.take) + 1;

  const [data, setData] = React.useState([]);
  const [isInAddMode, setIsInAddMode] = React.useState(false);
  const [showDeleteModel, setShowDeleteModel] = React.useState(false);
  const [deleteItem, setDeleteItem] = React.useState();
  const gridRef = React.useRef(null);

  const {
    isLoading: planMappingLoading,
    data: planMappingsResp,
    isFetching,
  } = useQueryGetPlanMappingPage(page, gridDataState.take, gridDataState.sort);
  const pagedData = planMappingsResp?.data;

  const { data: carrierListResp } = useQueryGetCarrierList();
  const { data: planListResp } = useQueryGetPlanListForMapping("MED");

  const carrierList = carrierListResp?.data || [];
  const planList = React.useMemo(
    () =>
      planListResp?.data?.map((e) => ({
        ...e,
        PlanDisName: `${e.planId}-${e.planDesc}`,
      })) || [],
    [planListResp],
  );

  const postMutation = useMutatePostPlanMapping();
  const putMutation = useMutatePutPlanMapping();
  const deleteMutation = useMutateDeletePlanMapping();

  React.useEffect(() => {
    if (!planMappingLoading && pagedData?.items) {
      setData(pagedData.items.map((item) => ({ ...item, [editField]: false })));
      setIsInAddMode(false);
    }
  }, [planMappingLoading, pagedData]);

  const itemChange = (event) => {
    const field = event.field || "";
    const newData = data.map((item) =>
      item.benefitPlanMappingId === event.dataItem.benefitPlanMappingId ||
      (isInAddMode && item === event.dataItem)
        ? { ...item, [field]: event.value }
        : item,
    );
    setData(newData);
  };

  const handleSave = async (dataItem) => {
    const carrierId = carrierList.find(
      (e) => e.carrierName === dataItem.carrierName,
    )?.carrierId;
    const payload = { ...dataItem, carrierId };
    if (dataItem.benefitPlanMappingId) {
      await putMutation.mutateAsync(payload);
    } else {
      await postMutation.mutateAsync(payload);
    }
  };

  const CommandCell = (props) => {
    const inEdit = props.dataItem[editField];
    const isNew = props.dataItem.benefitPlanMappingId === undefined;

    const onCancel = () => {
      if (isNew) {
        setData(data.slice(1));
        setIsInAddMode(false);
      } else {
        const originalItem = pagedData.items.find(
          (i) => i.benefitPlanMappingId === props.dataItem.benefitPlanMappingId,
        );
        setData(
          data.map((i) =>
            i.benefitPlanMappingId === props.dataItem.benefitPlanMappingId
              ? { ...originalItem, [editField]: false }
              : i,
          ),
        );
      }
    };

    return (
      <td className="k-command-cell">
        {inEdit ? (
          <>
            <Button
              onClick={() => handleSave(props.dataItem)}
              themeColor="primary"
              style={{ marginRight: "5px" }}
            >
              {isNew ? "Add" : "Update"}
            </Button>
            <Button onClick={onCancel}>{isNew ? "Discard" : "Cancel"}</Button>
          </>
        ) : (
          <>
            <Button
              disabled={isInAddMode}
              onClick={() =>
                setData(
                  data.map((i) =>
                    i.benefitPlanMappingId ===
                    props.dataItem.benefitPlanMappingId
                      ? { ...i, [editField]: true }
                      : i,
                  ),
                )
              }
              style={{ marginRight: "5px" }}
            >
              Edit
            </Button>
            <Button
              disabled={isInAddMode}
              onClick={() => {
                setDeleteItem(props.dataItem);
                setShowDeleteModel(true);
              }}
            >
              Remove
            </Button>
          </>
        )}
      </td>
    );
  };

  const loadingPanelMarkup = (
    <div className="k-loading-mask">
      <span className="k-loading-text">Loading</span>
      <div className="k-loading-image" />
      <div className="k-loading-color" />
    </div>
  );

  const LoadingPanel = (props) => {
    const { gridRef } = props;
    const gridContent =
      gridRef.current && gridRef.current.querySelector(".k-grid-content");
    return gridContent
      ? createPortal(loadingPanelMarkup, gridContent)
      : loadingPanelMarkup;
  };

  return (
    <>
      <ErrorBanner handler={postMutation} />
      {isFetching ? <LoadingPanel gridRef={gridRef} /> : null}
      <Grid
        style={{ height: "700px" }}
        data={data}
        total={pagedData?.totalCount || 0}
        {...gridDataState}
        onDataStateChange={(e) => setGridDataState(e.dataState)}
        onItemChange={itemChange}
        editField={editField}
        pageable={{ buttonCount: 5, pageSizes: [10, 20, 50] }}
        sortable={true}
        loading={isFetching}
        rowRender={(tr, props) =>
          React.cloneElement(tr, {
            style: props.dataItem[editField]
              ? { backgroundColor: "#fffdaf" }
              : {},
          })
        }
      >
        <GridToolbar>
          <Button
            themeColor="primary"
            onClick={() => {
              setData([
                {
                  [editField]: true,
                  carrierName: "",
                  planGroup: "",
                  planName: "",
                  prismPlanId: "",
                },
                ...data,
              ]);
              setIsInAddMode(true);
            }}
            disabled={isInAddMode}
          >
            Add new Mapping
          </Button>
        </GridToolbar>

        <Column
          field="carrierName"
          title="Carrier"
          width="200px"
          cell={(props) => <CarrierCell {...props} carrierList={carrierList} />}
        />
        <Column field="planGroup" title="Plan Group" cell={InputCell} />
        <Column field="planName" title="Plan Name" cell={InputCell} />
        <Column
          field="prismPlanId"
          title="Prism Plan"
          cell={(props) => <PlanListCell {...props} planList={planList} />}
        />
        <Column cell={CommandCell} width="180px" sortable={false} />
      </Grid>

      {showDeleteModel && (
        <DeleteDialog
          onDelete={async (item) => {
            await deleteMutation.mutateAsync(item);
            setShowDeleteModel(false);
          }}
          onCancel={() => setShowDeleteModel(false)}
          item={deleteItem}
        />
      )}
    </>
  );
};

export default PlanMapping;
